<script>
import { isEmpty } from 'lodash-es';
import {
  GlButton,
  GlCollapsibleListbox,
  GlExperimentBadge,
  GlIcon,
  GlSkeletonLoader,
  GlTooltipDirective,
} from '@gitlab/ui';
import { DUO_CHAT_AGENT_PLANNER } from '~/ai/constants';
import * as Sentry from '~/sentry/sentry_browser_wrapper';
import { createAlert } from '~/alert';
import { confirmAction } from '~/lib/utils/confirm_via_gl_modal/confirm_via_gl_modal';
import CrudComponent from '~/vue_shared/components/crud_component.vue';
import MarkdownEditor from '~/vue_shared/components/markdown/markdown_editor.vue';
import NonGfmMarkdown from '~/vue_shared/components/markdown/non_gfm_markdown.vue';
import { __, s__, sprintf } from '~/locale';
import { clearDraft, getDraft, updateDraft } from '~/lib/utils/autosave';
import { I18N_WORK_ITEM_ERROR_UPDATING } from '~/work_items/constants';
import { isWorkplanTemplate } from '~/work_items/utils';
import { findAgentPlanWidget } from 'ee/work_items/utils';
import { WIDGET_TYPE_AGENT_PLAN } from 'ee/work_items/constants';
import workplanTemplatesListQuery from '~/work_items/graphql/work_item_description_templates_list.query.graphql';
import workplanTemplateQuery from '~/work_items/graphql/work_item_description_template.query.graphql';
import namespacePathsQuery from '~/work_items/graphql/namespace_paths.query.graphql';
import updateWorkItemMutation from '~/work_items/graphql/update_work_item.mutation.graphql';
import glFeatureFlagsMixin from '~/vue_shared/mixins/gl_feature_flags_mixin';
import DuoChatQuickAction from 'ee/ai/shared/widgets/duo_chat_quick_action.vue';
import DuoWorkItemToMrAction from 'ee/ai/shared/widgets/duo_work_item_to_mr_action.vue';

// TODO: If we like this setup, we'd move this over to become a System Prompt of a Custom Agent.
// Then the user query can be more succinct, like "Prepare Workplan for Work Item ID #XYZ"
// Additionally, it should make use the the workplan templates that we define in https://gitlab.com/gitlab-org/gitlab/-/merge_requests/234690
const AGENTIC_PROMPT = s__(
  `AgentPlan|You are here to help refine a plan for the user from the Work Item context to a Markdown output with key information required for Agent to pick off the work. The template should have a Why, How, What format with key insights and list of steps. If the issue is lacking details, start by reading the comments. Then, ask clarifying questions to the users in Chat until you are confident. If there are pending questions, please preserve them inside the plan. Then, please proceed with updating the work item 'Workplan' widget with your output. You can erase everything was there before and add your new plan entirely. Don't preserve existing notes if they dont align with what you wrote. If there is enough information but the title or description is lacking, please propose to update it minimally with key insights. Try to preserve anything that is already present, just add on top and dont repeat the full plan. What is important is syncing up enough to preserve the intent and adding data where we had none before.`,
);

export default {
  name: 'AgentPlan',
  directives: {
    GlTooltip: GlTooltipDirective,
  },
  components: {
    CrudComponent,
    DuoChatQuickAction,
    DuoWorkItemToMrAction,
    GlButton,
    GlCollapsibleListbox,
    GlExperimentBadge,
    GlIcon,
    GlSkeletonLoader,
    MarkdownEditor,
    NonGfmMarkdown,
  },
  mixins: [glFeatureFlagsMixin()],
  inject: {
    hasRemoteFlowsEnabled: {
      from: 'duoRemoteFlowsAvailability',
      default: false,
    },
    fullPath: {
      default: '',
    },
  },
  props: {
    workItem: {
      type: Object,
      required: true,
    },
    canUpdate: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      isEditing: false,
      isCollapsed: false,
      markdownPaths: {},
      isSaving: false,
      tmpContent: '',
      formFieldProps: {
        id: 'agent-plan',
        name: 'agent-plan',
        'aria-label': s__('AgentPlan|Workplan'),
      },
      workplanTemplates: [],
      workplanTemplateContent: null,
      selectedWorkplanTemplate: null,
      templateSearchTerm: '',
      appliedTemplateName: null,
    };
  },
  computed: {
    workItemId() {
      return this.workItem?.id;
    },
    workItemType() {
      return this.workItem?.workItemType?.name;
    },
    agentPlanWidget() {
      return findAgentPlanWidget(this.workItem);
    },
    content() {
      return this.agentPlanWidget?.content || '';
    },
    canDeleteWorkPlan() {
      return this.canUpdate && !this.isEditing && this.content;
    },
    showGenerateMrAction() {
      return this.hasRemoteFlowsEnabled && this.content;
    },
    workItemIid() {
      return this.workItem?.iid;
    },
    workItemWebUrl() {
      // eslint-disable-next-line local-rules/no-web-url
      return this.workItem?.webUrl;
    },
    areMarkdownPathsLoaded() {
      return !isEmpty(this.markdownPaths);
    },
    planStorageKey() {
      return this.workItemId ? `agent-plan-draft-${this.workItemId}` : null;
    },
    templateStorageKey() {
      // eslint-disable-next-line @gitlab/require-i18n-strings
      return this.planStorageKey ? `${this.planStorageKey}-template` : null;
    },
    areWorkplanTemplatesLoading() {
      return this.$apollo.queries.workplanTemplates.loading;
    },
    hasWorkplanTemplates() {
      return this.workplanTemplates.some(({ name }) => isWorkplanTemplate(name));
    },
    workplanTemplateItems() {
      return this.workplanTemplates
        .filter(({ name }) => isWorkplanTemplate(name))
        .filter(({ name }) =>
          this.templateSearchTerm
            ? name.toLowerCase().includes(this.templateSearchTerm.toLowerCase())
            : true,
        )
        .reduce((groups, current) => {
          const category =
            // eslint-disable-next-line @gitlab/require-i18n-strings
            current.category === 'Project Templates'
              ? s__('WorkItem|Project Workplan Templates')
              : current.category;
          const idx = groups.findIndex((group) => group.text === category);
          const option = {
            value: this.makeWorkplanTemplateValue(current),
            text: current.name,
          };
          if (idx > -1) {
            groups[idx].options.push(option);
          } else {
            groups.push({
              text: category,
              options: [option],
            });
          }
          return groups;
        }, []);
    },
    selectedWorkplanTemplateValue() {
      if (!this.selectedWorkplanTemplate) {
        return undefined;
      }
      return this.makeWorkplanTemplateValue(this.selectedWorkplanTemplate);
    },
    workplanTemplateToggleText() {
      return this.selectedWorkplanTemplate?.name || s__('AgentPlan|Choose a workplan template');
    },
    optimisticResponse() {
      const existing = this.agentPlanWidget;
      const updatedWidget = {
        ...(existing || { type: WIDGET_TYPE_AGENT_PLAN, __typename: 'WorkItemWidgetAgentPlan' }),
        content: this.tmpContent,
      };
      const editedFeatures = this.workItem?.features
        ? {
            features: {
              ...this.workItem.features,
              agentPlan: { ...this.workItem.features.agentPlan, content: this.tmpContent },
            },
          }
        : {};
      return {
        workItemUpdate: {
          errors: [],
          workItem: {
            ...this.workItem,
            widgets: [
              ...(this.workItem.widgets || []).filter((w) => w.type !== WIDGET_TYPE_AGENT_PLAN),
              updatedWidget,
            ],
            ...editedFeatures,
          },
        },
      };
    },
  },
  created() {
    const { templateName } = this.loadFromStorage();
    if (templateName) {
      this.appliedTemplateName = templateName;
    }
  },
  apollo: {
    markdownPaths: {
      query: namespacePathsQuery,
      variables() {
        return {
          fullPath: this.fullPath,
          iidForMarkdownPreview: this.workItemIid,
        };
      },
      update(data) {
        return data?.namespace?.markdownPaths || {};
      },
      skip() {
        return !this.fullPath;
      },
    },
    workplanTemplates: {
      query: workplanTemplatesListQuery,
      variables() {
        return {
          fullPath: this.fullPath,
        };
      },
      update(data) {
        return data?.namespace?.workItemDescriptionTemplates?.nodes || [];
      },
      skip() {
        return !this.fullPath;
      },
      error(e) {
        Sentry.captureException(e);
      },
    },
    workplanTemplateContent: {
      query: workplanTemplateQuery,
      variables() {
        return {
          name: this.selectedWorkplanTemplate.name,
          projectId: this.selectedWorkplanTemplate.projectId,
          fromNamespace: this.fullPath,
        };
      },
      update(data) {
        return data?.workItemDescriptionTemplateContent?.content;
      },
      skip() {
        return !this.selectedWorkplanTemplate?.projectId;
      },
      result() {
        if (this.workplanTemplateContent != null) {
          this.applyWorkplanTemplate(this.workplanTemplateContent);
        }
      },
      error(e) {
        Sentry.captureException(e);
      },
    },
  },
  methods: {
    persistField(key, value) {
      if (!key) return;
      if (value) {
        updateDraft(key, value);
      } else {
        clearDraft(key);
      }
    },
    loadFromStorage() {
      return {
        content: this.planStorageKey ? getDraft(this.planStorageKey) : null,
        templateName: this.templateStorageKey ? getDraft(this.templateStorageKey) : null,
      };
    },
    clearStorage() {
      this.persistField(this.planStorageKey, null);
      this.persistField(this.templateStorageKey, null);
    },
    startEditing() {
      this.isEditing = true;
      this.isCollapsed = false;
      const { content: draftContent } = this.loadFromStorage();
      this.tmpContent = draftContent ?? this.content;
      if (this.appliedTemplateName) {
        this.selectedWorkplanTemplate = {
          name: this.appliedTemplateName,
          projectId: null,
          category: null,
        };
      }
    },
    cancelEdit() {
      this.isEditing = false;
      this.tmpContent = '';
      this.selectedWorkplanTemplate = null;
      this.persistField(this.planStorageKey, null);
    },
    showUpdateError(error) {
      createAlert({
        message: sprintf(I18N_WORK_ITEM_ERROR_UPDATING, {
          workItemType: this.workItemType,
        }),
      });
      Sentry.captureException(error);
    },
    async mutateAgentPlanContent() {
      const { data } = await this.$apollo.mutate({
        mutation: updateWorkItemMutation,
        variables: {
          input: {
            id: this.workItemId,
            agentPlanWidget: { content: this.tmpContent },
          },
          useWorkItemFeatures: Boolean(this.glFeatures?.workItemFeaturesField),
        },
        optimisticResponse: this.optimisticResponse,
      });

      if (data.workItemUpdate.errors.length) {
        throw new Error(data.workItemUpdate.errors.join('\n'));
      }
    },
    async savePlan() {
      const newTemplateName = this.selectedWorkplanTemplate?.name || null;

      this.isSaving = true;

      try {
        await this.mutateAgentPlanContent();
        this.isEditing = false;
        this.tmpContent = '';
        this.appliedTemplateName = newTemplateName;
        this.persistField(this.planStorageKey, null);
        this.persistField(this.templateStorageKey, newTemplateName);
      } catch (error) {
        this.showUpdateError(error);
      } finally {
        this.isSaving = false;
      }
    },
    async deletePlan() {
      const confirmed = await confirmAction(
        s__(
          'AgentPlan|Are you sure you want to delete this workplan? This action cannot be undone.',
        ),
        {
          primaryBtnText: __('Delete'),
          primaryBtnVariant: 'danger',
        },
      );
      if (!confirmed) return;

      const prevTmpContent = this.tmpContent;
      this.isSaving = true;
      this.appliedTemplateName = null;
      this.selectedWorkplanTemplate = null;
      this.clearStorage();
      this.tmpContent = '';

      try {
        await this.mutateAgentPlanContent();
      } catch (error) {
        this.tmpContent = prevTmpContent;
        this.showUpdateError(error);
      } finally {
        this.isSaving = false;
      }
    },
    handleInput(value) {
      this.tmpContent = value;
      this.persistField(this.planStorageKey, value);
    },
    handleSelectWorkplanTemplate(value) {
      try {
        const { name, projectId, category } = JSON.parse(value);
        this.selectedWorkplanTemplate = { name, projectId, category };
      } catch (e) {
        Sentry.captureException(e);
      }
    },
    handleWorkplanTemplateSearch(term) {
      this.templateSearchTerm = term;
    },
    applyWorkplanTemplate(content) {
      this.handleInput(content);
    },
    makeWorkplanTemplateValue({ name, category, projectId }) {
      return JSON.stringify({ name, category, projectId });
    },
  },
  duoQuickAction: {
    command: {
      agent: { name: DUO_CHAT_AGENT_PLANNER },
      agenticPrompt: AGENTIC_PROMPT,
    },
    tracking: { label: 'create_work_plan' },
    buttonOptions: { icon: 'tanuki-ai', category: 'primary' },
  },
};
</script>

<template>
  <crud-component
    :is-collapsible="true"
    :collapsed="isCollapsed"
    anchor-id="agent-plan"
    @collapsed="isCollapsed = true"
    @expanded="isCollapsed = false"
  >
    <template #title>
      <div class="gl-flex gl-flex-wrap gl-gap-4">
        {{ s__('AgentPlan|Workplan') }}
        <gl-icon name="tanuki-ai" variant="subtle" />
        <gl-experiment-badge class="!gl-mx-0 -gl-my-1 gl-self-center" />
      </div>
    </template>

    <template #actions>
      <div class="gl-flex gl-items-center gl-gap-4">
        <duo-work-item-to-mr-action
          v-if="showGenerateMrAction"
          :project-path="fullPath"
          :work-item-id="workItemIid"
          :work-item-type="workItemType"
          :work-item-web-url="workItemWebUrl"
        />
        <gl-button
          v-if="canUpdate && !isEditing"
          v-gl-tooltip
          icon="pencil"
          category="tertiary"
          size="small"
          :aria-label="s__('AgentPlan|Edit workplan')"
          :title="s__('AgentPlan|Edit workplan')"
          data-testid="edit-agent-plan-button"
          @click="startEditing"
        />
        <gl-button
          v-if="canDeleteWorkPlan"
          v-gl-tooltip
          icon="remove"
          category="tertiary"
          size="small"
          :aria-label="s__('AgentPlan|Delete workplan')"
          :title="s__('AgentPlan|Delete workplan')"
          :disabled="isSaving"
          data-testid="delete-agent-plan-button"
          @click="deletePlan"
        />
      </div>
    </template>

    <template v-if="!isEditing">
      <p v-if="!content" data-testid="agent-plan-empty-state" class="gl-mb-0 gl-text-subtle">
        {{
          canUpdate
            ? s__(
                'AgentPlan|Describe your intent for Duo. A workplan structures context into an agent-ready input for autonomous execution.',
              )
            : s__(
                "AgentPlan|No workplan has been added yet. You don't have permission to edit the workplan.",
              )
        }}
      </p>
      <div v-else data-testid="agent-plan-defined">
        <non-gfm-markdown :markdown="content" data-testid="agent-plan-rendered" />
      </div>
    </template>

    <template v-else>
      <div class="gl-mb-3 gl-flex gl-justify-end">
        <duo-chat-quick-action
          v-if="workItemId"
          :resource-id="workItemId"
          :button-text="s__('AgentPlan|Generate workplan with Duo')"
          :command="$options.duoQuickAction.command"
          :tracking-info="$options.duoQuickAction.tracking"
          :button-options="$options.duoQuickAction.buttonOptions"
          data-testid="generate-with-duo-button"
        />
      </div>
      <gl-skeleton-loader v-if="areWorkplanTemplatesLoading" />
      <gl-collapsible-listbox
        v-else-if="hasWorkplanTemplates"
        :items="workplanTemplateItems"
        :toggle-text="workplanTemplateToggleText"
        :selected="selectedWorkplanTemplateValue"
        :header-text="s__('AgentPlan|Select workplan template')"
        searchable
        block
        class="gl-mb-3 gl-w-30"
        data-testid="agent-plan-template-dropdown"
        @select="handleSelectWorkplanTemplate"
        @search="handleWorkplanTemplateSearch"
      >
        <template #list-item="{ item }">
          <span class="gl-break-words" data-testid="agent-plan-template-item">
            {{ item.text }}
          </span>
        </template>
      </gl-collapsible-listbox>
      <div class="common-note-form">
        <markdown-editor
          v-if="areMarkdownPathsLoaded"
          :value="tmpContent"
          :render-markdown-path="markdownPaths.markdownPreviewPath"
          :uploads-path="markdownPaths.uploadsPath"
          :form-field-props="formFieldProps"
          :enable-autocomplete="false"
          :autofocus="true"
          :disabled="isSaving"
          data-testid="agent-plan-editor"
          @input="handleInput"
          @keydown.meta.enter="savePlan"
          @keydown.ctrl.enter="savePlan"
        />
      </div>
      <div class="gl-mt-4 gl-flex gl-gap-3">
        <gl-button
          variant="confirm"
          :loading="isSaving"
          data-testid="save-agent-plan-button"
          @click="savePlan"
        >
          {{ __('Save changes') }}
        </gl-button>
        <gl-button :disabled="isSaving" data-testid="cancel-agent-plan-button" @click="cancelEdit">
          {{ __('Cancel') }}
        </gl-button>
      </div>
    </template>
  </crud-component>
</template>
